import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select"; // Required: npm install react-select

const DelegationModal = ({ show, initialData, onClose, onSave, users = [], documentTypes = [] }) => {
  const [delegator, setDelegator] = useState(null); // Stores object {value, label}
  const [delegatee, setDelegatee] = useState(null);
  const [functionName, setFunctionName] = useState("");
  const [documentTypeId, setDocumentTypeId] = useState("");
  const [validated, setValidated] = useState(false);

  // 1. Convert users list to react-select format { value, label }
  const userOptions = users.map(u => ({
    value: u.id,
    label: u.fullName || u.userName
  }));

  useEffect(() => {
    if (initialData) {
      // Find corresponding objects from options list when editing
      const currentDelegator = userOptions.find(opt => opt.value === initialData.delegatorId);
      const currentDelegatee = userOptions.find(opt => opt.value === initialData.delegateeId);
      
      setDelegator(currentDelegator || null);
      setDelegatee(currentDelegatee || null);
      setFunctionName(initialData.function || "");
      setDocumentTypeId(initialData.documentTypeId || "");
    } else {
      setDelegator(null);
      setDelegatee(null);
      setFunctionName("");
      setDocumentTypeId("");
    }
    setValidated(false);
  }, [initialData, show, users]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!delegator || !delegatee || !functionName || !documentTypeId) {
      setValidated(true);
      return;
    }

    onSave({
      id: initialData?.id,
      delegatorId: Number(delegator.value), // Get value (ID) from object
      delegateeId: Number(delegatee.value),
      function: functionName,
      documentTypeId: Number(documentTypeId),
    });
  };

  // Styles for react-select to match Bootstrap and look clean
  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '8px',
      borderColor: '#dee2e6',
      padding: '2px',
      boxShadow: 'none',
      '&:hover': { borderColor: '#0d6efd' }
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }) // Ensure menu appears above Modal
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="md" className="user-modal-custom">
      <Modal.Header closeButton className="bg-gradient-primary text-white py-3 border-0">
        <Modal.Title className="d-flex align-items-center">
          <div className="bg-white text-primary rounded-circle p-2 me-3 shadow-sm d-flex align-items-center justify-content-center" style={{width:'40px', height:'40px'}}>
            <i className="fas fa-handshake"></i>
          </div>
          <div>
            <div className="fw-bold h5 mb-0">{initialData ? "Update Delegation" : "Add New Delegation"}</div>
            <small className="opacity-75" style={{fontSize: '0.75rem'}}>Type name to search employee quickly</small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-light">
        <Form noValidate validated={validated}>
          <Row className="g-3">
            {/* Delegator */}
            <Col md={12}>
              <Form.Label className="small fw-bold text-muted text-uppercase">Delegator (Search by name) *</Form.Label>
              <Select
                options={userOptions}
                value={delegator}
                onChange={setDelegator}
                placeholder="-- Find Delegator --"
                isSearchable={true}
                styles={selectStyles}
                noOptionsMessage={() => "Employee not found"}
              />
              {validated && !delegator && <div className="text-danger small mt-1">Please select a delegator</div>}
            </Col>

            {/* Delegatee */}
            <Col md={12}>
              <Form.Label className="small fw-bold text-muted text-uppercase">Delegatee (Search by name) *</Form.Label>
              <Select
                options={userOptions}
                value={delegatee}
                onChange={setDelegatee}
                placeholder="-- Find Delegatee --"
                isSearchable={true}
                styles={selectStyles}
                noOptionsMessage={() => "Employee not found"}
              />
              {validated && !delegatee && <div className="text-danger small mt-1">Please select a delegatee</div>}
            </Col>

            {/* Function */}
            <Col md={6}>
              <Form.Label className="small fw-bold text-muted text-uppercase">Function *</Form.Label>
              <Form.Control 
                required 
                placeholder="Approve, Sign..." 
                value={functionName} 
                onChange={(e) => setFunctionName(e.target.value)} 
              />
            </Col>

            {/* Document Type */}
            <Col md={6}>
              <Form.Label className="small fw-bold text-muted text-uppercase">Document Type *</Form.Label>
              <Form.Select 
                required 
                value={documentTypeId} 
                onChange={(e) => setDocumentTypeId(e.target.value)}
              >
                <option value="">-- Select Type --</option>
                {documentTypes?.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="bg-white border-0 py-3 shadow-sm">
        <Button variant="link" className="text-muted text-decoration-none fw-medium" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          className="px-4 py-2 rounded-pill fw-bold bg-gradient-primary border-0 shadow-sm"
        >
          {initialData ? "Save Changes" : "Create Delegation"}
        </Button>
      </Modal.Footer>

      <style>{`
        .bg-gradient-primary { background: linear-gradient(135deg, #0d6efd 0%, #0099ff 100%); }
        .user-modal-custom .modal-content { border: none; border-radius: 16px; overflow: hidden; }
      `}</style>
    </Modal>
  );
};

export default DelegationModal;